import React, { FC, FormEvent, useCallback, useContext, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import { UserData } from '../../../common/data/user-data';
import BusinessContext from '../../../contexts/businessContext';
import { BusinessData } from '../../../common/data/business-data';
import { AdminData } from '../../../common/data/admin-data';
import { getLangWithKey, getOtherLanguage, ILang } from '../../../lang';
import showNotification from '../../../components/extras/showNotification';
import Icon from '../../../components/icon/Icon';
import useFunctions from '../../../hooks/useFunctions';

interface ResponseData {
	status: number;
	message: string;
	businessData: BusinessData;
	adminData: AdminData;
	businessList: BusinessData[];
}

interface ILoginHeaderProps {
	t: TFunction;
}
const LoginHeader: FC<ILoginHeaderProps> = ({ t }) => {
	return (
		<>
			<div className='text-center h1 fw-bold mt-5'>{t('LoginHeader')},</div>
			<div className='text-center h6 text-muted mb-5'>{t('LoginSubText')}</div>
		</>
	);
};

const Login = () => {
	const { setUser, setUid } = useContext(AuthContext);
	const [userToLogin, setUserToLogin] = useState<UserData>({} as UserData);
	const { business, setBusiness, setBusinessList } = useContext(BusinessContext);
	const { darkModeStatus } = useDarkMode();
	const [signInPassword, setSignInPassword] = useState<boolean>(false);

	const navigate = useNavigate();
	const { callFunction } = useFunctions();

	const handleOnClick = useCallback(
		() =>
			navigate(
				userToLogin && userToLogin.adminData && userToLogin.adminData.uid
					? '/admin-dashboard'
					: '/',
			),
		[navigate, userToLogin],
	);
	const auth = getAuth();
	const { t, i18n } = useTranslation('translation');

	const styledBtn: IButtonProps = {
		color: 'light',
		hoverShadow: 'default',
		isLight: true,
		size: 'lg',
	};

	const changeLanguage = (lng: ILang['key']['lng']) => {
		i18n.changeLanguage(lng).then();
		showNotification(
			<span className='d-flex align-items-center'>
				<Icon icon={getLangWithKey(lng)?.icon} size='lg' className='me-1' />
				<span>{getLangWithKey(lng)?.message}</span>
			</span>,
			'',
		);
	};

	const otherLanguage = useMemo(() => {
		return getOtherLanguage(i18n.language as ILang['key']['lng']);
	}, [i18n.language]);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginEmail: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginEmail?: string; loginPassword?: string } = {};

			if (!values.loginEmail) {
				errors.loginEmail = 'Required';
			}

			if (!values.loginPassword) {
				errors.loginPassword = 'Required';
			}

			return errors;
		},
		validateOnChange: false,
		onSubmit: (values) => {
			if (values.loginEmail && values.loginPassword) {
				signInWithEmailAndPassword(auth, values.loginEmail, values.loginPassword)
					.then(async (userCredential) => {
						// Signed in
						const firebaseUser = userCredential.user;
						setUser({
							uid: userToLogin.uid,
							adminData: userToLogin.adminData,
						});
						if (!userToLogin.uid) {
							setUid(firebaseUser.uid);

							await callFunction('saveBusinessUid', {
								uid: business?.uid,
							});
						}
						handleOnClick();
					})
					.catch((error) => {
						console.error('Error during signIn', error);
						window.alert(
							`${t('SignInError')} :\n\n ${error.code} \n\n ${error.message}`,
						);
					});
			}
		},
	});

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const handleContinue = () => {
		const email = formik.values.loginEmail;
		const functions = getFunctions();
		const checkBusinessByEmail = httpsCallable(functions, 'checkBusinessByEmail');
		checkBusinessByEmail({ email, language: i18n.language })
			.then((result: HttpsCallableResult) => {
				const { businessData, adminData, businessList } = result.data as ResponseData;
				setUserToLogin({
					uid: businessData?.uid || adminData?.uid,
					adminData,
				});
				setBusiness(businessData);
				setBusinessList(businessList);
				setSignInPassword(true);
				setIsLoading(false);
			})
			.catch(({ message }) => {
				formik.setFieldError('loginEmail', message);
				setIsLoading(false);
			});
	};

	const startOver = () => {
		setUserToLogin({} as UserData);
		setBusiness({} as BusinessData);
		setSignInPassword(false);
		formik.resetForm();
	};

	return (
		<PageWrapper isProtected={false} title='Login' className='bg-dark'>
			<Page className='p-0'>
				<div className='row h-100 align-items-center justify-content-center'>
					<div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
						<Card className='shadow-3d-dark' data-tour='login-page'>
							<CardBody>
								<div className='text-end'>
									<Button
										{...styledBtn}
										icon={otherLanguage.icon}
										onClick={() => changeLanguage(otherLanguage.lng)}
									/>
								</div>
								<div className='text-center my-5'>
									<Link
										to='/'
										className={classNames(
											'text-decoration-none  fw-bold display-2',
											{
												'text-dark': !darkModeStatus,
												'text-light': darkModeStatus,
											},
										)}
										aria-label='Facit'>
										<Logo isLogin />
									</Link>
								</div>

								<LoginHeader t={t} />

								<form className='row g-4'>
									<div className='col-12'>
										<FormGroup
											id='loginEmail'
											isFloating
											label={t('Email')}
											className={classNames({
												'd-none': signInPassword,
											})}>
											<Input
												type='email'
												value={formik.values.loginEmail}
												isTouched={formik.touched.loginEmail}
												invalidFeedback={formik.errors.loginEmail}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => {
													formik.setErrors({});
												}}
											/>
										</FormGroup>
										{signInPassword && (
											<div className='text-center h4 mb-3 fw-bold'>
												{t('Hello')},{' '}
												{business?.name || userToLogin?.adminData?.name}.
												<span className='d-block h6 text-muted'>
													{t('EnterPassword')}
												</span>
											</div>
										)}
										<FormGroup
											id='loginPassword'
											isFloating
											label='Password'
											className={classNames({
												'd-none': !signInPassword,
											})}>
											<Input
												type='password'
												autoComplete='current-password'
												value={formik.values.loginPassword}
												isTouched={formik.touched.loginPassword}
												invalidFeedback={formik.errors.loginPassword}
												validFeedback='Looks good!'
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
											/>
										</FormGroup>
									</div>
									<div className='col-12'>
										{!signInPassword ? (
											<Button
												color='warning'
												className='w-100 py-3'
												isDisable={!formik.values.loginEmail}
												onClick={handleContinue}>
												{isLoading && <Spinner isSmall inButton isGrow />}
												{t('LoginNext')}
											</Button>
										) : (
											<>
												<Button
													color='warning'
													className='w-100 py-3'
													onClick={(e: FormEvent) => {
														e.preventDefault();
														formik.handleSubmit();
													}}>
													{t('Login')}
												</Button>
												<Button
													color='link'
													className='w-100 py-3'
													onClick={startOver}>
													{t('StartOver')}
												</Button>
											</>
										)}
									</div>
									<div className='text-center mt-3 text-white'>
										<Link
											to='/auth-pages/forgot-password'
											className='text-decoration-none'>
											{t('ForgotPassword')}
										</Link>
									</div>
								</form>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
